import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('qantri_user_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/user/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('qantri_user_token', newToken);
    setToken(newToken);
    setUser(userData);
    
    // If user is admin, also save admin token for admin dashboard access
    if (userData.role === 'admin') {
      localStorage.setItem('qantri_admin_token', newToken);
    }
    
    return userData;
  };

  // Direct login with user data (used after PayPal callback)
  const loginWithData = (userData) => {
    setUser(userData);
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('qantri_user_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('qantri_user_token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  // Check if user has access to premium news
  const hasNewsAccess = () => {
    if (!user) return false;
    // Free for paid partner plans
    if (user.partner_plan && ['bronze', 'silver', 'gold', 'elite'].includes(user.partner_plan)) {
      return true;
    }
    // Or if has active news subscription
    if (user.subscription === 'news_premium') {
      return true;
    }
    return false;
  };

  return (
    <UserContext.Provider value={{
      user,
      token,
      loading,
      login,
      loginWithData,
      register,
      logout,
      refreshUser,
      hasNewsAccess,
      isAuthenticated: !!user
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);

export { UserContext };
